// =============================================================================
// PEDRA — src/state/store.tsx
// Estado global do projeto: React Context + useReducer, com historico de
// desfazer/refazer e autosave no localStorage.
//
// O reducer e PURO e nao toca em armazenamento: quem grava e o efeito de
// autosave dentro do <ProjectProvider>. Isso mantem o reducer testavel.
// =============================================================================

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import type { Dispatch, ReactElement, ReactNode } from 'react';

import type {
  EdgeBandType,
  Id,
  Part,
  Project,
  Settings,
  StockSheet,
} from '../core/types';
import {
  createDefaultSettings,
  createEmptyEdgeBandType,
  createEmptyPart,
  createEmptyProject,
  createEmptyStock,
  createSampleProject,
  newId,
  syncIdCounterFromProject,
} from '../core/defaults';
import { LIMITS } from '../core/validation';
import type { DebouncedSaver, SaveStatus } from './persistence';
import { AUTOSAVE_DELAY_MS, createDebouncedSaver, loadProject } from './persistence';

// -----------------------------------------------------------------------------
// ESTADO E ACOES
// -----------------------------------------------------------------------------

export interface ProjectState {
  project: Project;
  /** Pilha de desfazer, do mais antigo para o mais recente. */
  past: Project[];
  /** Pilha de refazer. Zerada a cada nova edicao. */
  future: Project[];
  /** true quando ha mudanca ainda nao gravada. */
  dirty: boolean;
}

export type ProjectAction =
  | { type: 'setProject'; project: Project }
  | { type: 'newProject' }
  | { type: 'loadSample' }
  | { type: 'setName'; name: string }
  | { type: 'addPart' }
  | { type: 'addParts'; parts: Part[]; replace?: boolean }
  | { type: 'updatePart'; id: Id; patch: Partial<Part> }
  | { type: 'removeParts'; ids: Id[] }
  | { type: 'duplicateParts'; ids: Id[] }
  | { type: 'moveParts'; ids: Id[]; toIndex: number }
  | { type: 'clearParts' }
  | { type: 'addStock' }
  | { type: 'addStockSheets'; sheets: StockSheet[]; replace?: boolean }
  | { type: 'updateStock'; id: Id; patch: Partial<StockSheet> }
  | { type: 'removeStock'; ids: Id[] }
  | { type: 'duplicateStock'; ids: Id[] }
  | { type: 'moveStock'; ids: Id[]; toIndex: number }
  | { type: 'clearStock' }
  | { type: 'updateSettings'; patch: Partial<Settings> }
  | { type: 'resetSettings' }
  | { type: 'addEdgeBand' }
  | { type: 'updateEdgeBand'; id: Id; patch: Partial<EdgeBandType> }
  | { type: 'removeEdgeBand'; id: Id }
  | { type: 'undo' }
  | { type: 'redo' }
  | { type: 'markSaved' };

/** Passos guardados no historico. Acima disso o mais antigo cai fora. */
export const HISTORY_LIMIT = 50;

// -----------------------------------------------------------------------------
// HELPERS DE LISTA
// -----------------------------------------------------------------------------

interface HasId {
  id: Id;
}

/** Remove as linhas cujos ids estao em `ids`. Devolve a MESMA lista se nada muda. */
function removeRows<T extends HasId>(rows: T[], ids: readonly Id[]): T[] {
  if (ids.length === 0) return rows;
  const set = new Set(ids);
  const out = rows.filter((r) => !set.has(r.id));
  return out.length === rows.length ? rows : out;
}

/** Insere uma copia logo abaixo de cada linha selecionada. */
function duplicateRows<T extends HasId>(
  rows: T[],
  ids: readonly Id[],
  clone: (row: T) => T,
): T[] {
  if (ids.length === 0) return rows;
  const set = new Set(ids);
  const out: T[] = [];
  for (const r of rows) {
    out.push(r);
    if (set.has(r.id) && out.length < LIMITS.MAX_ROWS) out.push(clone(r));
  }
  return out.length === rows.length ? rows : out;
}

/**
 * Move as linhas selecionadas para `toIndex`, preservando a ordem relativa
 * entre elas. `toIndex` e a posicao desejada na lista ORIGINAL.
 */
function moveRows<T extends HasId>(rows: T[], ids: readonly Id[], toIndex: number): T[] {
  if (ids.length === 0) return rows;
  const set = new Set(ids);
  const moving = rows.filter((r) => set.has(r.id));
  if (moving.length === 0) return rows;
  const rest = rows.filter((r) => !set.has(r.id));

  const target = Math.max(0, Math.min(Math.trunc(toIndex), rows.length));
  // Quantas linhas movidas estavam ANTES do alvo: o alvo desloca para tras.
  let before = 0;
  for (let i = 0; i < target; i += 1) {
    const row = rows[i] as T;
    if (set.has(row.id)) before += 1;
  }
  const insertAt = Math.max(0, Math.min(target - before, rest.length));
  const out = [...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)];

  // Nada mudou de lugar? Devolve a lista original para nao poluir o historico.
  let same = out.length === rows.length;
  if (same) {
    for (let i = 0; i < out.length; i += 1) {
      if (out[i] !== rows[i]) {
        same = false;
        break;
      }
    }
  }
  return same ? rows : out;
}

/**
 * Garante que as linhas recebidas de fora (colar, CSV, JSON) nao repitam ids
 * ja presentes no projeto. Corta no limite de linhas.
 */
function adoptRows<T extends HasId>(
  incoming: readonly T[],
  existing: readonly T[],
  prefix: string,
): T[] {
  const used = new Set(existing.map((r) => r.id));
  const out: T[] = [];
  for (const row of incoming) {
    if (out.length + existing.length >= LIMITS.MAX_ROWS) break;
    let id = typeof row.id === 'string' && row.id.trim() !== '' ? row.id : newId(prefix);
    while (used.has(id)) id = newId(prefix);
    used.add(id);
    out.push(id === row.id ? row : { ...row, id });
  }
  return out;
}

function clonePart(p: Part): Part {
  return { ...p, id: newId('p'), edgeBanding: { ...p.edgeBanding } };
}

function cloneStock(s: StockSheet): StockSheet {
  return { ...s, id: newId('s') };
}

/** Aplica um patch preservando o id e mesclando a fita de borda. */
function patchPart(p: Part, patch: Partial<Part>): Part {
  const next: Part = { ...p, ...patch, id: p.id };
  if (patch.edgeBanding) next.edgeBanding = { ...p.edgeBanding, ...patch.edgeBanding };
  return next;
}

// -----------------------------------------------------------------------------
// HISTORICO
// -----------------------------------------------------------------------------

/** Registra a mudanca no historico. Se o projeto nao mudou, nada acontece. */
function commit(state: ProjectState, next: Project): ProjectState {
  if (next === state.project) return state;
  const past =
    state.past.length >= HISTORY_LIMIT
      ? [...state.past.slice(state.past.length - HISTORY_LIMIT + 1), state.project]
      : [...state.past, state.project];
  return { project: next, past, future: [], dirty: true };
}

/** Atalho: troca so as pecas. */
function withParts(state: ProjectState, parts: Part[]): ProjectState {
  if (parts === state.project.parts) return state;
  return commit(state, { ...state.project, parts });
}

/** Atalho: troca so as chapas. */
function withStock(state: ProjectState, stock: StockSheet[]): ProjectState {
  if (stock === state.project.stock) return state;
  return commit(state, { ...state.project, stock });
}

// -----------------------------------------------------------------------------
// REDUCER
// -----------------------------------------------------------------------------

export function projectReducer(state: ProjectState, action: ProjectAction): ProjectState {
  const p = state.project;

  switch (action.type) {
    // --- projeto inteiro -----------------------------------------------------
    case 'setProject': {
      syncIdCounterFromProject(action.project);
      return commit(state, action.project);
    }

    case 'newProject': {
      // Projeto em branco, mas mantendo as preferencias do usuario
      // (unidade, kerf, cadastro de fitas). "Restaurar padrões" e outra acao.
      const blank = createEmptyProject();
      return commit(state, {
        ...blank,
        settings: { ...p.settings },
        edgeBands: p.edgeBands.map((b) => ({ ...b })),
      });
    }

    case 'loadSample': {
      // O exemplo traz as proprias configuracoes de corte, mas as preferencias
      // de EXIBICAO do usuario (unidade, casas decimais, ordem) sao mantidas.
      const sample = createSampleProject();
      return commit(state, {
        ...sample,
        settings: {
          ...sample.settings,
          unit: p.settings.unit,
          decimalPlaces: p.settings.decimalPlaces,
          dimensionOrder: p.settings.dimensionOrder,
        },
      });
    }

    case 'setName': {
      const name = action.name.slice(0, LIMITS.MAX_NAME_LENGTH);
      if (name === p.name) return state;
      return commit(state, { ...p, name });
    }

    // --- pecas ---------------------------------------------------------------
    case 'addPart': {
      if (p.parts.length >= LIMITS.MAX_ROWS) return state;
      return withParts(state, [...p.parts, createEmptyPart(p.parts.length)]);
    }

    case 'addParts': {
      if (action.parts.length === 0 && !action.replace) return state;
      const base = action.replace ? [] : p.parts;
      const adopted = adoptRows(action.parts, base, 'p');
      return withParts(state, [...base, ...adopted]);
    }

    case 'updatePart': {
      let changed = false;
      const parts = p.parts.map((row) => {
        if (row.id !== action.id) return row;
        changed = true;
        return patchPart(row, action.patch);
      });
      return changed ? withParts(state, parts) : state;
    }

    case 'removeParts':
      return withParts(state, removeRows(p.parts, action.ids));

    case 'duplicateParts':
      return withParts(state, duplicateRows(p.parts, action.ids, clonePart));

    case 'moveParts':
      return withParts(state, moveRows(p.parts, action.ids, action.toIndex));

    case 'clearParts':
      return p.parts.length === 0 ? state : withParts(state, []);

    // --- chapas --------------------------------------------------------------
    case 'addStock': {
      if (p.stock.length >= LIMITS.MAX_ROWS) return state;
      return withStock(state, [...p.stock, createEmptyStock()]);
    }

    case 'addStockSheets': {
      if (action.sheets.length === 0 && !action.replace) return state;
      const base = action.replace ? [] : p.stock;
      const adopted = adoptRows(action.sheets, base, 's');
      return withStock(state, [...base, ...adopted]);
    }

    case 'updateStock': {
      let changed = false;
      const stock = p.stock.map((row) => {
        if (row.id !== action.id) return row;
        changed = true;
        return { ...row, ...action.patch, id: row.id };
      });
      return changed ? withStock(state, stock) : state;
    }

    case 'removeStock':
      return withStock(state, removeRows(p.stock, action.ids));

    case 'duplicateStock':
      return withStock(state, duplicateRows(p.stock, action.ids, cloneStock));

    case 'moveStock':
      return withStock(state, moveRows(p.stock, action.ids, action.toIndex));

    case 'clearStock':
      return p.stock.length === 0 ? state : withStock(state, []);

    // --- configuracoes -------------------------------------------------------
    case 'updateSettings': {
      const settings: Settings = { ...p.settings, ...action.patch };
      let same = true;
      for (const key of Object.keys(action.patch) as Array<keyof Settings>) {
        if (settings[key] !== p.settings[key]) {
          same = false;
          break;
        }
      }
      if (same) return state;
      return commit(state, { ...p, settings });
    }

    case 'resetSettings':
      return commit(state, { ...p, settings: createDefaultSettings() });

    // --- fitas de borda ------------------------------------------------------
    case 'addEdgeBand': {
      if (p.edgeBands.length >= LIMITS.MAX_ROWS) return state;
      return commit(state, { ...p, edgeBands: [...p.edgeBands, createEmptyEdgeBandType()] });
    }

    case 'updateEdgeBand': {
      let changed = false;
      const edgeBands = p.edgeBands.map((b) => {
        if (b.id !== action.id) return b;
        changed = true;
        return { ...b, ...action.patch, id: b.id };
      });
      return changed ? commit(state, { ...p, edgeBands }) : state;
    }

    case 'removeEdgeBand': {
      const edgeBands = p.edgeBands.filter((b) => b.id !== action.id);
      if (edgeBands.length === p.edgeBands.length) return state;
      // Toda peca que usava esta fita perde a referencia.
      let touched = false;
      const parts = p.parts.map((part) => {
        const eb = part.edgeBanding;
        if (
          eb.top !== action.id &&
          eb.right !== action.id &&
          eb.bottom !== action.id &&
          eb.left !== action.id
        ) {
          return part;
        }
        touched = true;
        return {
          ...part,
          edgeBanding: {
            top: eb.top === action.id ? null : eb.top,
            right: eb.right === action.id ? null : eb.right,
            bottom: eb.bottom === action.id ? null : eb.bottom,
            left: eb.left === action.id ? null : eb.left,
          },
        };
      });
      return commit(state, { ...p, edgeBands, parts: touched ? parts : p.parts });
    }

    // --- historico -----------------------------------------------------------
    case 'undo': {
      if (state.past.length === 0) return state;
      const previous = state.past[state.past.length - 1] as Project;
      return {
        project: previous,
        past: state.past.slice(0, -1),
        future: [state.project, ...state.future].slice(0, HISTORY_LIMIT),
        dirty: true,
      };
    }

    case 'redo': {
      if (state.future.length === 0) return state;
      const next = state.future[0] as Project;
      return {
        project: next,
        past: [...state.past, state.project].slice(-HISTORY_LIMIT),
        future: state.future.slice(1),
        dirty: true,
      };
    }

    case 'markSaved':
      return state.dirty ? { ...state, dirty: false } : state;

    default:
      return state;
  }
}

// -----------------------------------------------------------------------------
// CONTEXTO
// -----------------------------------------------------------------------------

export interface ProjectContextValue {
  project: Project;
  dispatch: Dispatch<ProjectAction>;
  canUndo: boolean;
  canRedo: boolean;
  dirty: boolean;
  saveStatus: SaveStatus;
  saveError: string | null;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

/** Estado inicial: rascunho do localStorage ou projeto vazio. */
function createInitialState(): ProjectState {
  const stored = loadProject();
  const project = stored ?? createEmptyProject();
  syncIdCounterFromProject(project);
  return { project, past: [], future: [], dirty: false };
}

export function ProjectProvider(props: { children: ReactNode }): ReactElement {
  const [state, dispatch] = useReducer(projectReducer, undefined, createInitialState);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);

  // Gravador com debounce, criado uma unica vez.
  const saverRef = useRef<DebouncedSaver | null>(null);
  if (saverRef.current === null) {
    saverRef.current = createDebouncedSaver(AUTOSAVE_DELAY_MS, (status, error) => {
      setSaveStatus(status);
      setSaveError(status === 'error' ? (error ?? 'Falha ao salvar o projeto.') : null);
      if (status === 'saved') dispatch({ type: 'markSaved' });
    });
  }

  // Agenda a gravacao a cada mudanca real do projeto. A comparacao por
  // referencia tambem neutraliza a montagem dupla do StrictMode.
  const lastQueuedRef = useRef<Project>(state.project);
  useEffect(() => {
    if (state.project === lastQueuedRef.current) return;
    lastQueuedRef.current = state.project;
    saverRef.current?.schedule(state.project);
  }, [state.project]);

  // Grava na hora quando a aba some ou a pagina e descartada.
  useEffect(() => {
    const saver = saverRef.current;
    const onVisibility = (): void => {
      if (document.visibilityState === 'hidden') saver?.flush();
    };
    const onPageHide = (): void => saver?.flush();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', onPageHide);
      saver?.flush();
    };
  }, []);

  const value = useMemo<ProjectContextValue>(
    () => ({
      project: state.project,
      dispatch,
      canUndo: state.past.length > 0,
      canRedo: state.future.length > 0,
      dirty: state.dirty,
      saveStatus,
      saveError,
    }),
    [state.project, state.past.length, state.future.length, state.dirty, saveStatus, saveError],
  );

  return <ProjectContext.Provider value={value}>{props.children}</ProjectContext.Provider>;
}

/** Acessa o projeto e o dispatch. Precisa estar dentro de <ProjectProvider>. */
export function useProject(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (ctx === null) {
    throw new Error('useProject() precisa ser usado dentro de <ProjectProvider>.');
  }
  return ctx;
}
